import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Activity, AlertTriangle, FileText, RefreshCcw, Repeat2, TrendingUp, Users } from 'lucide-react';

function metricCard({ label, value, icon: Icon, note }) {
  return (
    <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</p>
            <div className="mt-2 text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</div>
            {note ? <p className="mt-1 text-xs text-slate-500">{note}</p> : null}
          </div>
          <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-700">
            <Icon size={18} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

export default function BudgetUsage() {
  const navigate = useNavigate();
  const now = new Date();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const load = async (nextPage = page) => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      params.set('month', String(month));
      params.set('year', String(year));
      params.set('page', String(nextPage));
      params.set('limit', '20');
      if (search.trim()) params.set('search', search.trim());
      const r = await api.get(`/admin/budget/usage?${params.toString()}`);
      setItems(r.data?.users || []);
      setSummary(r.data?.summary || {});
      setPage(r.data?.page || nextPage);
      setPages(r.data?.pages || 1);
      setTotal(r.data?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load budget usage');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(1); }, []);

  const onSearch = (event) => {
    event.preventDefault();
    setPage(1);
    load(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Budget Usage</h1>
          <p className="text-slate-500 text-sm mt-0.5">Monitor Budget transactions, branches, loans, credit and reports per user.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/budget-users')}>
            <Users size={14} className="mr-1.5" /> Budget Users
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/budget-packages')}>
            <Repeat2 size={14} className="mr-1.5" /> Budget Packages
          </Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => load(page)} disabled={refreshing}>
            <RefreshCcw size={14} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {metricCard({ label: 'Overspending Users', value: summary.overspending_users ?? 0, icon: AlertTriangle, note: 'Users who exceeded budget limits this period.' })}
        {metricCard({ label: 'Overdue Loans', value: summary.overdue_loans ?? 0, icon: TrendingUp, note: 'Loan records marked overdue.' })}
        {metricCard({ label: 'Overdue Credit', value: summary.overdue_credit ?? 0, icon: Activity, note: 'Credit records marked overdue.' })}
        {metricCard({ label: 'Reports Generated', value: summary.reports ?? 0, icon: FileText, note: 'Stored or cached reports.' })}
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="space-y-4">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Usage Breakdown</CardTitle>
          <form onSubmit={onSearch} className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="grid grid-cols-2 gap-3 lg:w-auto">
              <Input type="number" min="1" max="12" value={month} onChange={(e) => setMonth(Number(e.target.value) || month)} placeholder="Month" />
              <Input type="number" min="2020" value={year} onChange={(e) => setYear(Number(e.target.value) || year)} placeholder="Year" />
            </div>
            <div className="flex-1">
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email or phone" />
            </div>
            <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white">Search</Button>
          </form>
          <div className="flex flex-wrap gap-2 text-xs text-slate-500">
            <div className="rounded-full bg-slate-50 border border-slate-200 px-3 py-1">Total users: {total}</div>
            <div className="rounded-full bg-slate-50 border border-slate-200 px-3 py-1">Budget-enabled users: {summary.budget_enabled_users ?? 0}</div>
            <div className="rounded-full bg-slate-50 border border-slate-200 px-3 py-1">Transactions: {formatNumber(summary.transactions)}</div>
            <div className="rounded-full bg-slate-50 border border-slate-200 px-3 py-1">Branches: {formatNumber(summary.branches)}</div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Budget</TableHead>
                <TableHead>Transactions</TableHead>
                <TableHead>Branches</TableHead>
                <TableHead>Loans</TableHead>
                <TableHead>Credit</TableHead>
                <TableHead>Overspending</TableHead>
                <TableHead>Overdue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="font-medium text-slate-900">{user.name}</div>
                    <div className="text-xs text-slate-500">{user.email || user.phone || user.id}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className={user.budget_enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}>
                      {user.budget_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                    <div className="text-xs text-slate-500 mt-1">{user.package_name || 'Free'} {user.days_left ? `· ${user.days_left} days left` : ''}</div>
                  </TableCell>
                  <TableCell>{formatNumber(user.usage?.transactions)}</TableCell>
                  <TableCell>{formatNumber(user.usage?.branches)}</TableCell>
                  <TableCell>{formatNumber(user.usage?.loans)}</TableCell>
                  <TableCell>{formatNumber(user.usage?.credit)}</TableCell>
                  <TableCell>
                    <Badge className={user.overspending ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}>
                      {user.overspending ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-xs text-slate-600">
                      <div>Loans: {formatNumber(user.overdue_loans)}</div>
                      <div>Credit: {formatNumber(user.overdue_credit)}</div>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!items.length && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-slate-500 py-10">
                    No budget usage data found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>Page {page} of {pages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => load(page - 1)}>Previous</Button>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => load(page + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
}
